import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  deleteUserOperatorDocument,
  ingestUserOperatorDocument,
  listUserOperatorDocuments,
  presignUserOperatorDocument,
} from "../api/client";

const MAX_PDF_BYTES = 12 * 1024 * 1024;

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "ready") return "success";
  if (s === "error") return "error";
  return "default";
};

const UserOperatorDocsSection = ({ userId }) => {
  const { t } = useTranslation("settings");
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [docTitle, setDocTitle] = useState("");
  const [localError, setLocalError] = useState("");
  const [uploading, setUploading] = useState(false);

  const uid = Number(userId);
  const enabled = Number.isFinite(uid) && uid > 0;

  const docsQuery = useQuery(
    ["userOperatorDocuments", uid],
    () => listUserOperatorDocuments(uid),
    {
      enabled,
      refetchInterval: (rows) =>
        Array.isArray(rows) && rows.some((r) => String(r?.status || "").toLowerCase() === "processing")
          ? 3500
          : false,
    }
  );

  const items = Array.isArray(docsQuery.data) ? docsQuery.data : [];

  const deleteMutation = useMutation(
    (docId) => deleteUserOperatorDocument(uid, docId),
    {
      onSuccess: () => qc.invalidateQueries(["userOperatorDocuments", uid]),
    }
  );

  const pickErrorMessage = (err) => {
    if (!err) return t("addUserForm.userOperatorDocs.uploadFailed");
    if (typeof err.message === "string" && err.message) return err.message;
    if (typeof err.error === "string" && err.error) return err.error;
    return t("addUserForm.userOperatorDocs.uploadFailed");
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLocalError("");

    const ct = file.type || "";
    const name = String(file.name || "").toLowerCase();
    const looksPdf = ct === "application/pdf" || name.endsWith(".pdf");
    if (!looksPdf) {
      setLocalError(t("addUserForm.userOperatorDocs.pdfOnly"));
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setLocalError(t("addUserForm.userOperatorDocs.tooLarge"));
      return;
    }

    setUploading(true);
    try {
      const presign = await presignUserOperatorDocument(uid, {
        filename: file.name,
        contentType: "application/pdf",
      });
      const uploadUrl = presign.uploadUrl;
      const key = presign.key;
      if (!uploadUrl || !key) {
        throw new Error(t("addUserForm.userOperatorDocs.presignFailed"));
      }

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(t("addUserForm.userOperatorDocs.s3UploadFailed"));
      }

      await ingestUserOperatorDocument(uid, {
        s3Key: key,
        titulo: (docTitle || "").trim() || file.name,
        arquivoNome: file.name,
      });

      setDocTitle("");
      await qc.invalidateQueries(["userOperatorDocuments", uid]);
    } catch (err) {
      setLocalError(pickErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  if (!enabled) return null;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <PictureAsPdfOutlinedIcon fontSize="small" color="action" />
            <span>{t("addUserForm.userOperatorDocs.title")}</span>
          </Stack>
        }
        subheader={t("addUserForm.userOperatorDocs.subtitle")}
      />
      <CardContent sx={{ pt: 0 }}>
        {localError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError("")}>
            {localError}
          </Alert>
        ) : null}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }} sx={{ mb: 2 }}>
          <TextField
            label={t("addUserForm.userOperatorDocs.labelTitle")}
            placeholder={t("addUserForm.userOperatorDocs.titlePlaceholder")}
            value={docTitle}
            onChange={(ev) => setDocTitle(ev.target.value)}
            size="small"
            fullWidth
            sx={{ flex: 1 }}
            disabled={uploading}
          />
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={onPickFile}
          />
          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {t("addUserForm.userOperatorDocs.upload")}
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t("addUserForm.userOperatorDocs.hint")}
        </Typography>

        {docsQuery.isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("addUserForm.userOperatorDocs.loadingList")}
            </Typography>
          </Box>
        ) : docsQuery.isError ? (
          <Alert severity="warning">{t("addUserForm.userOperatorDocs.listError")}</Alert>
        ) : !items.length ? (
          <Typography variant="body2" color="text.secondary">
            {t("addUserForm.userOperatorDocs.empty")}
          </Typography>
        ) : (
          <List dense disablePadding>
            {items.map((row) => (
              <ListItem
                key={row.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={t("addUserForm.userOperatorDocs.delete")}
                    onClick={() => deleteMutation.mutate(row.id)}
                    disabled={deleteMutation.isLoading}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ pr: 7, alignItems: "flex-start" }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
                      <Typography variant="body2" component="span">
                        {row.titulo || row.arquivo_nome || "—"}
                      </Typography>
                      <Chip
                        size="small"
                        label={t(
                          `addUserForm.userOperatorDocs.status.${
                            ["processing", "ready", "error"].includes(String(row.status || "").toLowerCase())
                              ? String(row.status || "").toLowerCase()
                              : "unknown"
                          }`
                        )}
                        color={statusColor(row.status)}
                        variant="outlined"
                      />
                      {row.status === "ready" && row.chunk_count != null ? (
                        <Typography variant="caption" color="text.secondary" component="span">
                          {t("addUserForm.userOperatorDocs.chunks", { count: row.chunk_count })}
                        </Typography>
                      ) : null}
                    </Stack>
                  }
                  secondary={
                    <>
                      <Typography variant="caption" color="text.secondary" component="span" display="block">
                        {row.arquivo_nome}
                      </Typography>
                      {row.status === "error" && row.erro_msg ? (
                        <Typography variant="caption" color="error" component="span" display="block">
                          {row.erro_msg}
                        </Typography>
                      ) : null}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default UserOperatorDocsSection;
